import { API_CONFIG, API_ENDPOINTS } from '../config/api.js';

/**
 * Base fetch wrapper with timeout and error handling
 */
const fetchWithTimeout = async (url, options = {}, timeout = API_CONFIG.TIMEOUT) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });

    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection.');
    }
    throw error;
  }
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Invalid response format from server');
  }

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }

  return data;
};

/**
 * Scan QR code and get payment details
 * @param {string} txRef - Transaction reference from QR code
 * @returns {Promise<Object>} Payment details with available chains
 */
export const scanQR = async (txRef) => {
  if (!txRef || typeof txRef !== 'string') {
    throw new Error('Invalid transaction reference');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SCAN_QR(txRef)}`;
  
  try {
    console.log('🔍 Calling API: POST', url);
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const contentType = response.headers.get('content-type');
    
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server');
    }

    const data = await response.json();
    
    console.log('✅ API Response:', data);
    
    // ✅ CHANGED: Check for valid flag instead of response.ok
    // Backend returns {valid: false, reason: '...'} for invalid payments
    if (!data.valid) {
      console.log('❌ Payment not valid:', data.reason);
      // Return the data so frontend can handle the specific reason
      return data;
    }

    // ✅ SUCCESS: Payment is valid
    if (!data.payment) {
      throw new Error('Invalid API response: missing payment data');
    }

    const requiredFields = [
      'txRef',
      'tokenName',
      'genericToken',
      'cryptoAmount',
      'fiatCurrency',
      'fiatAmount',
      'conversionRate',
      'merchantWallet',
      'expiresAt',
      'availableChains'
    ];

    for (const field of requiredFields) {
      if (data.payment[field] === undefined || data.payment[field] === null) {
        throw new Error(`Invalid API response: missing field "${field}"`);
      }
    }

    if (!Array.isArray(data.payment.availableChains) || data.payment.availableChains.length === 0) {
      throw new Error('No available chains for this payment');
    }

    for (const chain of data.payment.availableChains) {
      if (!chain.chainId || !chain.name || !chain.whitelistedTokens || !chain.metamask) {
        throw new Error('Invalid chain configuration from backend');
      }
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    // Re-throw the error for the frontend to handle
    throw error;
  }
};





/*OLD
 
export const scanQR = async (txRef) => {
  if (!txRef || typeof txRef !== 'string') {
    throw new Error('Invalid transaction reference');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SCAN_QR(txRef)}`;
  
  try {
    console.log('🔍 Calling API: POST', url);
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await parseResponse(response);
    
    console.log('✅ API Response:', data);
    
    if (!data.valid) {
      throw new Error(data.reason || 'Payment not valid');
    }

    if (!data.payment) {
      throw new Error('Invalid API response: missing payment data');
    }

    const requiredFields = [
      'txRef',
      'tokenName',
      'genericToken',
      'cryptoAmount',
      'fiatCurrency',
      'fiatAmount',
      'conversionRate',
      'merchantWallet',
      'expiresAt',
      'availableChains'
    ];

    for (const field of requiredFields) {
      if (data.payment[field] === undefined || data.payment[field] === null) {
        throw new Error(`Invalid API response: missing field "${field}"`);
      }
    }

    if (!Array.isArray(data.payment.availableChains) || data.payment.availableChains.length === 0) {
      throw new Error('No available chains for this payment');
    }

    for (const chain of data.payment.availableChains) {
      if (!chain.chainId || !chain.name || !chain.whitelistedTokens || !chain.metamask) {
        throw new Error('Invalid chain configuration from backend');
      }
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    if (error.message.includes('not_found')) {
      throw new Error('Payment not found. Please scan a valid QR code.');
    }
    
    if (error.message.includes('expired')) {
      throw new Error('Payment has expired. Please request a new payment.');
    }
    
    if (error.message.includes('scanned') || error.message.includes('source_selected') || error.message.includes('paid')) {
      throw new Error('Payment already processed. Please request a new payment.');
    }

    throw error;
  }
};
*/



/**
 * Select source chain and send token balances
 * @param {string} txRef - Transaction reference
 * @param {number} sourceChain - Selected chain ID
 * @param {string} customerWallet - Customer wallet address
 * @param {Array} tokenBalances - Array of {symbol, balance, address}
 * @returns {Promise<Object>} Payment options
 */
export const selectSourceChain = async (txRef, sourceChain, customerWallet, tokenBalances) => {
  if (!txRef || !sourceChain) {
    throw new Error('Missing required parameters: txRef and sourceChain');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SELECT_SOURCE_CHAIN}`;
  
  try {
    console.log('📤 Calling API: POST', url, {
      txRef,
      sourceChain,
      customerWallet,
      tokenBalances
    });
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        txRef,
        sourceChain,
        customerWallet,
        tokenBalances
      })
    });

    const contentType = response.headers.get('content-type');
    
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server');
    }

    const data = await response.json();
    
    console.log('✅ API Response:', data);
    
    // ✅ CHANGED: Don't throw on !response.ok, return the error data
    // This allows frontend to handle specific error codes
    if (!data.success) {
      console.log('❌ Backend returned error:', data.code, data.error);
      // Return the full error response so frontend can handle specific codes
      return {
        success: false,
        error: data.error,
        code: data.code,
        reason: data.reason,
        currentState: data.currentState
      };
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    // For network/timeout errors, throw as before
    if (error.message.includes('timeout') || error.message.includes('network')) {
      throw error;
    }
    
    // For other errors, return structured error
    return {
      success: false,
      error: error.message,
      code: 'NETWORK_ERROR'
    };
  }
};




/* OLD

export const selectSourceChain = async (txRef, sourceChain, customerWallet, tokenBalances) => {
  if (!txRef || !sourceChain) {
    throw new Error('Missing required parameters: txRef and sourceChain');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SELECT_SOURCE_CHAIN}`;
  
  try {
    console.log('📤 Calling API: POST', url, {
      txRef,
      sourceChain,
      customerWallet,
      tokenBalances
    });
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        txRef,
        sourceChain,
        customerWallet,
        tokenBalances
      })
    });

    const data = await parseResponse(response);
    
    console.log('✅ API Response:', data);
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to select source chain');
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    if (error.message.includes('expired')) {
      throw new Error('Payment has expired. Please request a new payment.');
    }
    
    if (error.message.includes('Invalid payment state')) {
      throw new Error('Payment is in an invalid state. Please start over.');
    }

    throw error;
  }
};
*/

/**
 * Execute payment - Submit signature to backend
 * @param {string} txRef - Transaction reference
 * @param {number} optionIndex - Selected payment option index
 * @param {string} signature - EIP-712 signature from wallet
 * @param {Object} permit2Data - Complete Permit2 payload
 * @returns {Promise<Object>} Signed payment confirmation
 */
export const executePayment = async (txRef, optionIndex, signature, permit2Data) => {
  if (!txRef || optionIndex === undefined || !signature || !permit2Data) {
    throw new Error('Missing required parameters: txRef, optionIndex, signature, permit2Data');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EXECUTE_PAYMENT}`;
  
  try {
    console.log('📤 Calling API: POST', url, {
      txRef,
      optionIndex,
      signatureLength: signature.length,
      permit2Keys: Object.keys(permit2Data)
    });
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        txRef,
        optionIndex,
        signature,
        permit2Data
      })
    });

    const contentType = response.headers.get('content-type');
    
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server');
    }

    const data = await response.json();
    
    console.log('✅ API Response:', data);
    
    // ✅ CHANGED: Don't throw on !response.ok, return the error data
    // This allows frontend to handle specific error codes
    if (!data.success) {
      console.log('❌ Backend returned error:', data.code, data.error);
      // Return the full error response so frontend can handle specific codes
      return {
        success: false,
        error: data.error,
        code: data.code,
        reason: data.reason,
        currentState: data.currentState,
        details: data.details,
        state: data.state,
        payment: data.payment,
        cancelReason: data.cancelReason
      };
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    // For network/timeout errors, throw as before
    if (error.message.includes('timeout') || error.message.includes('network')) {
      throw error;
    }
    
    // For other errors, return structured error
    return {
      success: false,
      error: error.message,
      code: 'NETWORK_ERROR'
    };
  }
};



/* OLD

export const executePayment = async (txRef, optionIndex, signature, permit2Data) => {
  if (!txRef || optionIndex === undefined || !signature || !permit2Data) {
    throw new Error('Missing required parameters: txRef, optionIndex, signature, permit2Data');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EXECUTE_PAYMENT}`;
  
  try {
    console.log('📤 Calling API: POST', url, {
      txRef,
      optionIndex,
      signatureLength: signature.length,
      permit2Keys: Object.keys(permit2Data)
    });
    
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        txRef,
        optionIndex,
        signature,
        permit2Data
      })
    });

    const data = await parseResponse(response);
    
    console.log('✅ API Response:', data);
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to execute payment');
    }

    return data;
    
  } catch (error) {
    console.error('❌ API Error:', error);
    
    if (error.message.includes('expired')) {
      throw new Error('Payment has expired. Please request a new payment.');
    }
    
    if (error.message.includes('Invalid payment state')) {
      throw new Error('Payment is in an invalid state. Please start over.');
    }
    
    if (error.message.includes('already signed')) {
      throw new Error('This payment has already been signed.');
    }
    
    if (error.message.includes('mismatch') || error.message.includes('tampering')) {
      throw new Error('Payment validation failed. Please restart the payment process.');
    }

    throw error;
  }
};

*/




/**
 * Check payment status
 * @param {string} txRef - Transaction reference
 * @returns {Promise<Object>} Payment status
 */
export const checkPayment = async (txRef) => {
  if (!txRef) {
    throw new Error('Transaction reference required');
  }

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CHECK_PAYMENT(txRef)}`;
  
  try {
    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await parseResponse(response);
    
    if (!data.found) {
      throw new Error('Payment not found');
    }

    return data;
    
  } catch (error) {
    console.error('❌ Check Payment Error:', error);
    throw error;
  }
};